import { getEyeAbleUrl } from '@/helpers/functions';
import type { eyeAbleUrlMapping } from '@helpers/constants';
import {
  Avatar,
  Card,
  Button,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Link,
} from '@eye-able/ui';
import type { LucideIcon } from 'lucide-react';
import { RouterLink } from '@/components/RouterLink';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';

export interface ProductCardProps {
  product: string;
  link: string;
  icon: LucideIcon;
  learnMoreLink: keyof typeof eyeAbleUrlMapping;
}

function ProductCard({
  product,
  link,
  icon: Icon,
  learnMoreLink,
}: ProductCardProps) {
  const { t, i18n } = useInlineTranslation();

  const learnMoreHref = getEyeAbleUrl(learnMoreLink, i18n.resolvedLanguage);

  return (
    <Card className='eye:flex eye:h-full eye:flex-col'>
      <CardHeader>
        <div className='eye:flex eye:justify-between eye:items-center'>
          <CardTitle headingLevel={2}>Eye-Able {product}</CardTitle>
          <Avatar className='eye:size-32 eye:bg-black/5'>
            <Icon aria-hidden className='eye:stroke-[1.5]' />
          </Avatar>
        </div>
        <CardDescription className='eye:mt-s'>
          <T k={`demoCardText+${product}`} />
        </CardDescription>
      </CardHeader>

      <CardFooter className='eye:mt-auto eye:flex eye:w-full eye:flex-col eye:items-start eye:gap-s'>
        {learnMoreHref && (
          <Link size='small' isExternal href={learnMoreHref}>
            {t('learnMoreAbout')} Eye-Able {product}
          </Link>
        )}
        <Button asChild className='eye:w-full' variant='yellow'>
          <RouterLink to={link}>
            {t('useNow', {
              product: `Eye-Able ${product}`,
            })}
          </RouterLink>
        </Button>
      </CardFooter>
    </Card>
  );
}

export default ProductCard;
